import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  createStyles,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { PFXSubItemData, PFXSubItemChosenOption, PFXSubItemOption } from "../../../../models/Interfaces";
import { selectCleverlynk, currencyFormatter } from "../../../../redux/slices/cleverlynkSlice";
import { selectCurrency } from "../../../../redux/slices/templateSlice";

import { useTranslation } from "react-i18next";
import "../../../../i18n/config";

interface ISubitemCheckbox {
  limit: number;
  subitem: PFXSubItemData;
  chosen: PFXSubItemChosenOption[];
  setChosen: Function;
  textFontFamily: string;
  skuMap: Map<string, number>;
}

function isNumber(value) {
  return typeof value === "number" && isFinite(value);
}

const SubitemCheckbox = ({ limit, subitem, chosen, setChosen, textFontFamily, skuMap }: ISubitemCheckbox) => {
  const currency = useSelector(selectCurrency);
  const cleverlynk = useSelector(selectCleverlynk);

  const calculatePriceWithoutDiscount = useCallback(
    value => {
      if (cleverlynk?.discount && value && value > 0) {
        return (100 * value) / (100 - cleverlynk?.discount);
      }
      return 0;
    },
    [cleverlynk]
  );

  const { t } = useTranslation("common");

  const useStyles = makeStyles(() =>
    createStyles({
      label: {
        flexGrow: 1,
      },
    })
  );

  const classes = useStyles();

  const onCheck = (option: PFXSubItemOption, controlsInventory: boolean) => {
    if (isChecked(option.id)) {
      setChosen(
        [...chosen].filter(o => o.id !== option.id),
        subitem.id
      );
    } else {
      let new_chosen = [...chosen];
      //@ts-ignore
      delete option.tableData;
      new_chosen.push({ ...option, subitemId: subitem.id!, quantity: 1, controlsInventory, sku: option.sku ?? "" });
      setChosen(new_chosen, subitem.id);
    }
  };

  // param - option id
  const isChecked = id => {
    return chosen.findIndex(o => o.id === id) !== -1;
  };

  return (
    <Grid item xs={12} key={`checkbox-${subitem.id}`} style={{ margin: "5px 0px" }} className="subitem-render-div">
      <Paper>
        <Box p={2} pr={0}>
          <FormControl component="fieldset" style={{ width: "100%" }}>
            <FormLabel component="legend" style={{ width: "100%" }}>
              <Grid container justifyContent="flex-start">
                <Grid item container xs={12} justifyContent="flex-start">
                  <Typography variant="body1" style={{ fontFamily: textFontFamily, textAlign: "left" }}>
                    {subitem.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="overline"
                    style={{
                      fontFamily: textFontFamily,
                      marginRight: (subitem?.choiceLimit ?? 0) < (subitem?.options ?? []).length ? 10 : "",
                    }}>
                    {(subitem?.choiceLimit ?? 0) < (subitem?.options ?? []).length &&
                      `${t("choose")} ${subitem.choiceLimit}`}
                  </Typography>
                  {subitem.mandatory && (
                    <Chip
                      variant="outlined"
                      color="secondary"
                      size="small"
                      style={{ height: 20 }}
                      label={
                        <Typography variant="overline" style={{ fontFamily: "Inter", fontSize: "0.65rem" }}>
                          {t("mandatory")}
                        </Typography>
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </FormLabel>
            <FormGroup>
              {subitem.options?.map(option =>
                !option.hidden ? (
                  <FormControlLabel
                    key={`checkbox-${subitem.id}-${option.label}`}
                    style={{ marginRight: 0 }}
                    control={
                      <Checkbox
                        color={
                          (limit <= chosen.length && !isChecked(option.id)) || skuMap.get(option.sku!) === 0
                            ? "default"
                            : "secondary"
                        }
                        onChange={() => onCheck(option, subitem.controlsInventory || false)}
                        checked={isChecked(option.id)}
                        name={option.label}
                        disabled={(limit <= chosen.length && !isChecked(option.id)) || skuMap.get(option.sku!) === 0}
                      />
                    }
                    classes={{
                      label: classes.label,
                    }}
                    label={
                      <Grid container alignItems="center" wrap="nowrap">
                        <Grid item style={{ marginRight: 5, flexGrow: 1 }}>
                          <Typography
                            style={{
                              marginRight: 9,
                              wordBreak: "break-word",
                              textAlign: "left",
                              fontFamily: textFontFamily,
                              fontSize: "0.9rem",
                            }}>
                            {option.label}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          container
                          justifyContent="flex-end"
                          style={{
                            display: option.sku && option.sku !== "" && !(skuMap.get(option.sku!) === 0) ? "none" : "",
                            flex: 1,
                          }}>
                          {cleverlynk?.discount && (
                            <Typography
                              variant="overline"
                              style={{
                                fontFamily: textFontFamily,
                                paddingRight: 16,
                                textAlign: "right",
                                opacity: 0.7,
                                MozOpacity: 0.7,
                                textDecoration: "line-through",
                                // paddingRight: 8,
                                textDecorationColor: "rgba(250, 0, 0, 1)",
                              }}>
                              {isNumber(option.value) && option.value! > 0
                                ? `${currencyFormatter(calculatePriceWithoutDiscount(option.value!), currency)}`
                                : ""}
                            </Typography>
                          )}
                          <Typography
                            variant="overline"
                            style={{ fontFamily: textFontFamily, paddingRight: 16, textAlign: "right" }}>
                            {isNumber(option.value) && option.value! > 0
                              ? `+${currencyFormatter(option.value!, currency)}`
                              : ""}
                          </Typography>
                        </Grid>
                        <Grid item style={{ display: skuMap.get(option.sku!) === 0 ? "" : "none" }}>
                          <Typography variant="overline" style={{ paddingLeft: 5 }}>
                            {t("noMoreUnits")}
                          </Typography>
                        </Grid>
                      </Grid>
                    }
                  />
                ) : null
              )}
            </FormGroup>
          </FormControl>
        </Box>
      </Paper>
    </Grid>
  );
};

export default SubitemCheckbox;
